'use client';

import { useEffect, useState } from 'react';
import { TableOfContents } from './table-of-contents';
import { extractHeadingsFromDOM } from '@/lib/blog/extract-headings';
import type { Heading } from '@/types/blog';

/**
 * Client component wrapper for Table of Contents that extracts headings from the DOM.
 *
 * This component runs client-side to extract headings from the rendered blog content
 * and passes them to the TableOfContents component. The headings are extracted after
 * the content has been rendered, ensuring all headings are available.
 *
 * The blog content itself is rendered server-side, so the headings exist in the HTML,
 * but we need client-side JavaScript to extract them for the TOC navigation.
 *
 * @example
 * ```tsx
 * <TableOfContentsClient />
 * ```
 *
 * @see {@link TableOfContents} - Display component for TOC navigation
 * @see {@link extractHeadingsFromDOM} - Utility function for heading extraction
 * @see {@link BlogPostLayoutServer} - Server component that uses this for TOC
 */
export function TableOfContentsClient() {
  const [headings, setHeadings] = useState<Heading[]>([]);

  useEffect(() => {
    const extractHeadings = () => {
      const blogContent = document.querySelector('article.max-w-none .blog-content');
      const extracted = extractHeadingsFromDOM(blogContent);
      if (extracted.length > 0) {
        setHeadings(extracted);
      }
    };

    // Try immediately
    extractHeadings();

    // Single delayed attempt for dynamically rendered content
    const timeoutId = setTimeout(extractHeadings, 200);

    // Use MutationObserver to watch for DOM changes
    // Only extract if heading elements are added/removed to avoid excessive calls
    const observer = new MutationObserver((mutations) => {
      // Check if any mutations involve heading elements
      const hasHeadingChanges = mutations.some((mutation) => {
        const addedHasHeadings = Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            ((node as Element).matches('h2, h3') || (node as Element).querySelector('h2, h3'))
        );
        const removedHasHeadings = Array.from(mutation.removedNodes).some(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            ((node as Element).matches('h2, h3') || (node as Element).querySelector('h2, h3'))
        );
        return addedHasHeadings || removedHasHeadings;
      });

      if (hasHeadingChanges) {
        extractHeadings();
      }
    });

    const blogContent = document.querySelector('article.max-w-none .blog-content');
    if (blogContent) {
      observer.observe(blogContent, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, []);

  if (headings.length === 0) return null;

  return <TableOfContents headings={headings} />;
}
